import React from 'react';
import { Accordion, AccordionDetails, AccordionSummary, Box, Card, Typography } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DataTable from '../../../sharedModules/dataTable';
import { DepartmentGroupableInvoice, DepartmentInvoiceGroup } from '../../clients/utils/groupInvoices';
import { formatDollar } from '../../clients/utils/format';

export const compactTableStyles = {
  headRow: {
    style: {
      minHeight: '42px',
    },
  },
  headCells: {
    style: {
      paddingTop: '6px',
      paddingBottom: '6px',
    },
  },
  rows: {
    style: {
      minHeight: '40px',
    },
  },
  cells: {
    style: {
      paddingTop: '6px',
      paddingBottom: '6px',
    },
  },
};

export const departmentAccordionSx = {
  border: '1px solid',
  borderColor: 'divider',
  borderRadius: 1,
  overflow: 'hidden',
  boxShadow: 1,
  '&:before': { display: 'none' },
};

interface Props<T extends DepartmentGroupableInvoice> {
  groups: DepartmentInvoiceGroup<T>[];
  columns: object[];
  summaryText: React.ReactNode;
  emptyMessage: string;
  keyField?: string;
}

function DepartmentGroupedInvoiceSections<T extends DepartmentGroupableInvoice>({
  groups,
  columns,
  summaryText,
  emptyMessage,
  keyField = 'apply_to',
}: Props<T>) {
  if (groups.length === 0) {
    return <Box sx={{ p: 3, color: 'text.secondary' }}>{emptyMessage}</Box>;
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Card>
        <Box
          sx={{
            px: 2,
            py: 1.25,
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: 1,
            bgcolor: 'grey.50',
          }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'text.secondary' }}>
            {summaryText}
          </Typography>
        </Box>
      </Card>
      {groups.map((group) => (
        <Accordion key={group.label} disableGutters defaultExpanded={false} sx={departmentAccordionSx}>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            sx={{
              px: 2,
              minHeight: 48,
              '& .MuiAccordionSummary-content': { alignItems: 'center', gap: 1, my: 1 },
            }}
          >
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'text.primary' }}>
              {group.label}
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
              · {group.count} {group.count === 1 ? 'invoice' : 'invoices'}
            </Typography>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'error.main' }}>
              · {formatDollar(group.total)}
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 0 }}>
            <DataTable
              keyField={keyField}
              columns={columns}
              data={group.invoices}
              highlightOnHover
              striped
              noHeader
              dense
              customStyles={compactTableStyles}
            />
          </AccordionDetails>
        </Accordion>
      ))}
    </Box>
  );
}

export default DepartmentGroupedInvoiceSections;
